import { useCallback, useEffect, useMemo, useState } from 'react';
import { Printer, Trash2, ClipboardList, ClipboardCheck, Plus } from '../lib/icons';
import { toast } from 'sonner';
import { fetchOfs, supprimerOf, fetchLotsPlanifiables, planifierLots, nomTiers, decompte, labelQualite, labelMode, familleLot, fileAttenteLot } from '../lib/dataProvider';
import { fmt, PageTitle, ParcoursBadge } from '../components/shared/badges';
import { couleurGain } from '../lib/couleurs';
import { PrintDoc } from '../components/print/PrintDoc';
import { RetourAtelierDialog } from '../components/planning/RetourAtelierDialog';
import { SaisieRapideOf } from '../components/planning/SaisieRapideOf';
import { SaisieGroupeeOf } from '../components/planning/SaisieGroupeeOf';
import { PeriodeFiltre, dansPeriode } from '../components/shared/PeriodeFiltre';
import { useAuth } from '../lib/auth';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const DECANTEURS = ['Décanteur 1', 'Décanteur 2', 'Décanteur 3'];

// 4 files d'attente strictes : parcours × filière. Routage automatique du bon validé.
const FILES = [
  { id: 'client_noble', label: 'Client Extra', hint: 'Prestations — Vierge / Extra-Vierge', accent: 'border-l-sky-600', chip: 'bg-sky-50 text-sky-800 border-sky-300' },
  { id: 'client_lampante', label: 'Client Lampante', hint: 'Prestations — Lampante', accent: 'border-l-orange-500', chip: 'bg-orange-50 text-orange-800 border-orange-300' },
  { id: 'fournisseur_noble', label: 'Fournisseur Extra', hint: 'Achats d’olives — Vierge / Extra-Vierge', accent: 'border-l-emerald-600', chip: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
  { id: 'fournisseur_lampante', label: 'Fournisseur Lampante', hint: 'Achats d’olives — Lampante', accent: 'border-l-red-600', chip: 'bg-red-50 text-red-800 border-red-300' },
];

export default function PlanningAtelier() {
  const { user } = useAuth();
  const [ofs, setOfs] = useState([]);
  const [lots, setLots] = useState([]);
  const [sel, setSel] = useState([]);
  const [decanteur, setDecanteur] = useState(DECANTEURS[0]);
  const [apercu, setApercu] = useState(null);
  const [retour, setRetour] = useState(null);
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [selOf, setSelOf] = useState([]);
  const [groupee, setGroupee] = useState(false);

  const load = useCallback(() => {
    fetchOfs().then(setOfs);
    fetchLotsPlanifiables().then(setLots);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const [file, setFile] = useState('client_noble');
  const parFile = useMemo(() => {
    const m = { client_noble: [], client_lampante: [], fournisseur_noble: [], fournisseur_lampante: [] };
    lots.forEach((r) => m[fileAttenteLot(r)]?.push(r));
    return m;
  }, [lots]);
  const lotsFile = parFile[file] ?? [];
  const sansQualite = lots.filter((r) => r.sansQualite);
  const voyagesParClient = useMemo(() => {
    const m = {};
    lotsFile.filter((r) => r.parcours === 'client').forEach((r) => { (m[r.tiersId] = m[r.tiersId] || []).push(r); });
    return m;
  }, [lotsFile]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selection = lots.filter((r) => sel.includes(r.id));
  const tonnage = selection.reduce((s, r) => s + decompte(r).poidsNet, 0);

  const creer = async () => {
    const { error, of } = await planifierLots(sel, user?.name, { decanteur });
    if (error) return toast.error(error);
    toast.success(`OF ${of.numero} créé — ${sel.length} lot(s) · ${decanteur}`);
    setSel([]);
    load();
  };
  const annuler = async (o) => {
    await supprimerOf(o.id);
    toast.success(`Ordre ${o.numero} annulé — lots remis en attente`);
    load();
  };

  const ouverts = ofs.filter((o) => o.statut !== 'Terminé');
  // Historique complet : tous les OF, avec alerte rouge sur les non traités / incomplets.
  const historique = ofs.filter((o) => dansPeriode(o.termineLe || o.date, du, au));

  const LigneLot = ({ r }) => (
    <label key={r.id} data-testid={`lot-planif-${r.id}`} className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 ${sel.includes(r.id) ? 'bg-slate-100' : ''}`}>
      <input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 accent-slate-900" />
      <span className="font-data font-semibold text-slate-900">{r.id}</span>
      <span className="text-slate-700">{nomTiers(r.tiersId)}</span>
      <span className="font-data text-xs text-slate-500">{r.qualite ? labelQualite(r.qualite) : '—'} · {labelMode(r.mode)} · {familleLot(r) === 'lampante' ? 'LAMPANTE' : 'noble'}</span>
      <span className="ml-auto font-data text-sm">{fmt(decompte(r).poidsNet)} kg</span>
    </label>
  );

  return (
    <div data-testid="planning-page" className="space-y-6">
      <PageTitle emoji="📋" titre="Planning atelier — Ordres de fabrication" sous="Dictez à l'atelier ce qui passe aux décanteurs, imprimez la fiche, saisissez le retour d'atelier" />

      <section data-testid="creer-of" className="border border-slate-200 border-l-4 border-l-slate-900 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <h2 className="font-heading text-base font-bold text-slate-900">Nouvel ordre de fabrication <span className="font-data text-xs font-normal text-slate-500">— {lots.length} lot(s) en attente</span></h2>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="of-decanteur" value={decanteur} onChange={(e) => setDecanteur(e.target.value)} className="h-10 border border-slate-300 bg-slate-50 px-3 font-data text-sm">
              {DECANTEURS.map((d) => <option key={d}>{d}</option>)}
            </select>
            <button type="button" data-testid="of-creer" disabled={!sel.length} onClick={creer} className="flex h-10 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white hover:bg-slate-700 disabled:bg-slate-300">
              <Plus size={15} /> Créer l'OF ({sel.length} lot(s) · {fmt(tonnage)} kg)
            </button>
          </div>
        </div>
        <div data-testid="files-attente" className="flex flex-wrap gap-px border-b border-slate-200 bg-slate-200">
          {FILES.map((f) => {
            const n = (parFile[f.id] ?? []).length;
            const actif = file === f.id;
            return (
              <button
                key={f.id}
                type="button"
                data-testid={`file-${f.id}`}
                onClick={() => { setFile(f.id); setSel([]); }}
                className={`flex-1 px-3 py-2.5 text-left transition-colors duration-150 ${actif ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                <p className="font-heading text-sm font-bold">
                  {f.label} <span className={`ml-1 border px-1.5 font-data text-[10px] font-bold ${actif ? 'border-white/40 text-white' : f.chip}`}>{n}</span>
                </p>
                <p className={`font-data text-[10px] uppercase tracking-[0.06em] ${actif ? 'text-slate-300' : 'text-slate-500'}`}>{f.hint}</p>
              </button>
            );
          })}
        </div>
        {sansQualite.length > 0 && (
          <p data-testid="planning-sans-qualite" className="border-b-2 border-red-600 bg-red-50 px-4 py-2 text-xs font-bold text-red-800">
            {sansQualite.length} bon(s) sans qualité non routable(s) : {sansQualite.map((r) => r.id).join(', ')} — complétez la qualité depuis Réception pour les faire apparaître dans une file.
          </p>
        )}
        <div data-testid={`liste-file-${file}`}>
          <p className="flex items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
            {FILES.find((f) => f.id === file)?.label} — {lotsFile.length} lot(s) en attente
            {lotsFile.length > 0 && (
              <button type="button" data-testid="of-tout-file" onClick={() => setSel(lotsFile.map((r) => r.id))} className="text-slate-900 underline">
                tout sélectionner
              </button>
            )}
          </p>
          {lotsFile.length === 0 && <p className="px-3 pb-3 text-xs text-slate-500">Aucun lot dans cette file.</p>}
          {file.startsWith('client')
            ? Object.entries(voyagesParClient).map(([tid, rs]) => (
                <div key={tid} className="border-b border-slate-100">
                  {rs.length > 1 && (
                    <button type="button" data-testid={`grouper-client-${tid}`} onClick={() => setSel(rs.map((r) => r.id))} className="mx-3 mt-2 border border-sky-300 bg-sky-50 px-2 py-0.5 text-[11px] font-bold text-sky-700">
                      {rs.length} voyages de {nomTiers(tid)} — grouper
                    </button>
                  )}
                  {rs.map((r) => <LigneLot key={r.id} r={r} />)}
                </div>
              ))
            : lotsFile.map((r) => <LigneLot key={r.id} r={r} />)}
        </div>
      </section>

      <section data-testid="of-ouverts" className="space-y-4">
        <h2 className="font-heading text-base font-bold text-slate-900">OF ouverts <span className="font-data text-xs text-slate-500">({ouverts.length})</span></h2>
        {ouverts.length > 0 && (
          <div data-testid="of-selection-groupee" className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 bg-white px-4 py-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                data-testid="of-tout-selectionner"
                checked={selOf.length === ouverts.length}
                onChange={(e) => setSelOf(e.target.checked ? ouverts.map((o) => o.id) : [])}
                className="h-4 w-4 accent-emerald-700"
              />
              Sélectionner tous les clients ({ouverts.reduce((s, o) => s + o.lignes.filter((l) => l.statut !== 'Trituré' && l.statut !== 'Décompté').length, 0)} lot(s) en attente)
            </label>
            <button
              type="button"
              data-testid="of-saisie-groupee"
              disabled={!selOf.length}
              onClick={() => setGroupee((g) => !g)}
              className="flex h-10 items-center gap-2 bg-emerald-700 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <ClipboardCheck size={15} /> {groupee ? 'Fermer la grille' : `Saisie Groupée (${selOf.length} OF)`}
            </button>
          </div>
        )}
        {groupee && selOf.length > 0 && (
          <SaisieGroupeeOf ofs={ouverts.filter((o) => selOf.includes(o.id))} onDone={load} />
        )}
        {ouverts.length === 0 && <p data-testid="planning-vide" className="border-2 border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">Aucun OF en cours.</p>}
        {ouverts.map((o) => (
          <section key={o.id} data-testid={`of-${o.id}`} className="border border-slate-200 border-l-2 border-l-slate-900 bg-white">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-heading text-base font-bold text-slate-900">
                  <input
                    type="checkbox"
                    data-testid={`of-selection-${o.id}`}
                    checked={selOf.includes(o.id)}
                    onChange={() => setSelOf((s) => (s.includes(o.id) ? s.filter((x) => x !== o.id) : [...s, o.id]))}
                    className="mr-2 h-4 w-4 accent-emerald-700"
                  />
                  <ClipboardList size={15} className="mr-1.5 inline" /> OF {o.numero} <span className="ml-2 border border-violet-300 bg-violet-50 px-1.5 font-data text-[11px] text-violet-700">{o.decanteur ?? '—'}</span>
                </p>
                <p className="font-data text-xs text-slate-500">{o.date} · {o.lignes.length} lot(s) · {fmt(o.olives)} kg d'olives · {o.creePar} · {o.statut}</p>
                {o.enAlerte && (
                  <p data-testid={`of-ouvert-alerte-${o.id}`} className="mt-1 border-l-4 border-red-600 bg-red-50 px-2 py-1 font-data text-[11px] font-bold text-red-800">
                    {o.alertes.join(' · ')}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" data-testid={`imprimer-of-${o.id}`} onClick={() => setApercu(o)} className="flex h-10 items-center gap-2 border border-slate-300 px-3 text-sm font-bold text-slate-700 hover:bg-slate-900 hover:text-white"><Printer size={15} /> Fiche papier</button>
                <button type="button" data-testid={`retour-of-${o.id}`} onClick={() => setRetour(o)} className="flex h-10 items-center gap-2 bg-emerald-700 px-3 font-heading text-sm font-bold text-white hover:bg-emerald-800"><ClipboardCheck size={15} /> Retour d'atelier</button>
                <button type="button" data-testid={`annuler-of-${o.id}`} onClick={() => annuler(o)} className="flex h-10 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:border-red-600 hover:bg-red-600 hover:text-white"><Trash2 size={15} /></button>
              </div>
            </div>
            <table className="w-full text-left">
              <thead className="bg-slate-50"><tr>{['#', 'Lot', 'Parcours', 'Tiers', 'Qualité', 'Mode', 'Olives (kg)', 'Statut', 'Huile (kg)', 'Cuve'].map((c) => <th key={c} className={th}>{c}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {o.lignes.map((l) => (
                  <tr key={l.id} className="text-sm">
                    <td className="px-3 py-1.5 font-data">{l.ordre}</td><td className="px-3 py-1.5 font-data font-semibold">{l.id}</td>
                    <td className="px-3 py-1.5"><ParcoursBadge parcours={l.parcours} /></td><td className="px-3 py-1.5">{l.tiers}</td>
                    <td className="px-3 py-1.5 text-xs">{l.qualite}</td><td className="px-3 py-1.5 text-xs">{l.mode}</td>
                    <td className="px-3 py-1.5 font-data">{fmt(l.poidsNet)}</td><td className="px-3 py-1.5 text-xs">{l.statut}</td>
                    <td className="px-3 py-1.5 font-data">{l.huile ? fmt(l.huile, 1) : '—'}</td><td className="px-3 py-1.5 text-xs">{l.cuves || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!(groupee && selOf.includes(o.id)) && <SaisieRapideOf of={o} onDone={load} />}
          </section>
        ))}
      </section>

      <section data-testid="of-termines" className="border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <h2 className="font-heading text-base font-bold text-slate-900">Historique des OF <span className="font-data text-xs text-slate-500">({historique.length} · {historique.filter((o) => o.enAlerte).length} en alerte)</span></h2>
          <PeriodeFiltre du={du} au={au} setDu={setDu} setAu={setAu} testid="periode-of" />
        </div>
        <table className="w-full text-left">
          <thead className="bg-slate-50"><tr>{['OF', 'Alerte', 'Créé', 'Terminé', 'Décanteur', 'Fournisseurs / clients', 'Filière', 'Olives (kg)', 'Huile (kg)', 'Rendement', 'Coût olives', 'Coût / kg huile', 'Écart vs marché', 'Bonus / Malus fosse', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {historique.length === 0 && <tr><td colSpan={15} className="px-3 py-8 text-center text-sm text-slate-500">Aucun OF sur la période.</td></tr>}
            {historique.map((o) => (
              <tr key={o.id} data-testid={`of-termine-${o.id}`} className={`text-sm ${o.enAlerte ? 'bg-red-50' : ''}`}>
                <td className={`px-3 py-2 font-data font-semibold ${o.enAlerte ? 'border-l-4 border-l-red-600 text-red-800' : ''}`}>{o.numero}</td>
                <td data-testid={`of-alerte-${o.id}`} className="px-3 py-2">
                  {o.enAlerte ? (
                    <span title={o.alertes.join(' · ')} className="border border-red-600 bg-red-600 px-1.5 py-0.5 font-data text-[10px] font-bold uppercase tracking-[0.06em] text-white">
                      {o.statut === 'Terminé' ? 'Incomplet' : 'Non traité'}
                    </span>
                  ) : (
                    <span className="border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 font-data text-[10px] font-bold uppercase text-emerald-700">OK</span>
                  )}
                </td>
                <td className="px-3 py-2 font-data text-xs">{o.date}</td><td className="px-3 py-2 font-data text-xs">{o.termineLe ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{o.decanteur ?? '—'}</td>
                <td data-testid={`of-tiers-${o.id}`} className="max-w-[220px] px-3 py-2 text-xs">{o.tiersNoms} <span className="font-data text-slate-400">({o.lignes.length} lot{o.lignes.length > 1 ? 's' : ''})</span></td>
                <td className="px-3 py-2"><span className={`border px-1.5 font-data text-[10px] font-bold uppercase ${o.famille === 'lampante' ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-emerald-300 bg-emerald-50 text-emerald-700'}`}>{o.famille}</span></td>
                <td className="px-3 py-2 font-data">{fmt(o.olives)}</td><td className="px-3 py-2 font-data">{fmt(o.huile, 1)}</td>
                <td className="px-3 py-2 font-data font-bold">{o.rendement != null ? `${fmt(o.rendement, 2)} %` : '—'}</td>
                <td className="px-3 py-2 font-data">{o.coutOlives ? `${fmt(o.coutOlives, 3)} TND` : '—'}</td>
                <td className="px-3 py-2 font-data font-bold">{o.coutRevientKg != null ? `${fmt(o.coutRevientKg, 3)} TND` : '—'}</td>
                <td data-testid={`of-ecart-${o.id}`} className={`px-3 py-2 font-data font-bold ${couleurGain(o.ecartMarche, '')}`}>
                  {o.ecartMarche == null ? '—' : `${o.ecartMarche >= 0 ? 'Bonus +' : 'Malus −'}${fmt(Math.abs(o.ecartMarche), 3)} TND`}
                  {o.ecartMarche != null && <span className="block text-[10px] font-normal text-slate-500">réf. {fmt(o.prixRef, 3)} TND/kg</span>}
                </td>
                <td data-testid={`of-fosse-${o.id}`} className={`px-3 py-2 font-data ${!o.fosse ? 'text-slate-400' : couleurGain(o.fosse.kg)}`}>
                  {o.fosse ? `${o.fosse.kg >= 0 ? '+' : '−'}${fmt(Math.abs(o.fosse.kg), 1)} kg · ${o.fosse.tnd >= 0 ? '+' : '−'}${fmt(Math.abs(o.fosse.tnd), 3)} TND` : '—'}
                </td>
                <td className="px-3 py-2 text-right"><button type="button" data-testid={`imprimer-of-hist-${o.id}`} onClick={() => setApercu(o)} className="border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-900 hover:text-white"><Printer size={13} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {retour && <RetourAtelierDialog of={retour} utilisateur={user?.name} onClose={() => setRetour(null)} onDone={load} />}
      {apercu && (
        <PrintDoc
          type="Ordre de fabrication — atelier"
          numero={apercu.numero}
          date={apercu.date}
          format="a4"
          tiers={`${apercu.decanteur ?? 'Décanteur —'} · établi par ${apercu.creePar}`}
          lignes={[
            ['Lots à traiter / tonnage', `${apercu.lignes.length} lot(s) · ${fmt(apercu.olives)} Kg d'olives`],
            ...apercu.lignes.map((l) => [
              `Passage ${l.ordre} — ${l.id} · ${l.tiers}`,
              `${l.qualite} · ${l.mode} · ${fmt(l.poidsNet)} Kg   |   Huile produite (Kg) : ____________   |   ${l.parcours === 'client' && l.devenir === 'sortie' ? 'SORTIE IMMÉDIATE — remettre au client, sans cuve' : l.parcours === 'client' && !l.devenir ? 'DESTINATION À CONFIRMER avant sortie' : 'Cuve de destination : ____________'}`, 
            ]),
          ]}
          mentions="Fiche papier pour le Chef d'Atelier : inscrire au stylo l'huile produite et, uniquement pour les lots à stocker, la cuve, puis reporter dans l'ERP via « Retour d'atelier »."
          signatures={['Visa — Direction / Pont-Bascule', 'Visa — Chef d’Atelier']}
          onClose={() => setApercu(null)}
        />
      )}
    </div>
  );
}
